import getApiBase from "../../utils/apiBase";
import type {
  AccountProfileResponse,
  AccountProfileUpdate,
  ProfileView,
  SignInMethod,
} from "./accountProfile.types";

const clean = (value: string | null | undefined): string => (value ?? "").trim();

const firstLetter = (value: string): string => (value ? Array.from(value)[0].toUpperCase() : "");

/** First letter of first + last name; falls back to the email's first letter. */
export const toInitials = (firstName: string, lastName: string, email: string): string => {
  const initials = `${firstLetter(clean(firstName))}${firstLetter(clean(lastName))}`;
  return initials || firstLetter(clean(email));
};

export const toSignInMethod = (profile: AccountProfileResponse): SignInMethod => {
  if (profile.oauth_provider) return { kind: "oauth", provider: profile.oauth_provider };
  if (profile.has_password) return { kind: "password" };
  return { kind: "link" };
};

/**
 * Photos the API hosts come back as an `/api/...` path, served by the API host; OAuth pictures are
 * already absolute. Other site paths (the app's own images) stay as they are.
 */
export const toAvatarUrl = (picture: string | null | undefined): string | null => {
  const value = clean(picture);
  if (!value) return null;
  return value.startsWith("/api/") ? `${getApiBase()}${value}` : value;
};

export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export type AvatarFileIssue = "type" | "size" | null;

/** Quick browser-side check; the server re-checks the file's real bytes. */
export const checkAvatarFile = (file: { type: string; size: number }): AvatarFileIssue => {
  if (!(AVATAR_TYPES as readonly string[]).includes(file.type)) return "type";
  if (file.size > AVATAR_MAX_BYTES) return "size";
  return null;
};

export const toProfileView = (profile: AccountProfileResponse): ProfileView => {
  const firstName = clean(profile.first_name);
  const lastName = clean(profile.last_name);
  const email = clean(profile.email);
  return {
    email,
    firstName,
    lastName,
    displayName: [firstName, lastName].filter(Boolean).join(" ") || email,
    initials: toInitials(firstName, lastName, email),
    avatarUrl: toAvatarUrl(profile.profile_picture),
    signInMethod: toSignInMethod(profile),
    hasPassword: profile.has_password === true,
  };
};

/** Only the fields that differ from the saved profile, trimmed. Empty object = nothing to save. */
export const toProfileUpdate = (
  saved: Pick<ProfileView, "firstName" | "lastName">,
  draft: { firstName: string; lastName: string },
): AccountProfileUpdate => {
  const update: AccountProfileUpdate = {};
  if (clean(draft.firstName) !== saved.firstName) update.first_name = clean(draft.firstName);
  if (clean(draft.lastName) !== saved.lastName) update.last_name = clean(draft.lastName);
  return update;
};

/** The backend needs 1..80 characters per name. */
export const isValidName = (value: string): boolean => {
  const length = clean(value).length;
  return length >= 1 && length <= 80;
};

/** "google" -> "Google". Provider ids come from the backend, so no names are listed here. */
export const toProviderLabel = (provider: string): string =>
  provider ? `${provider.charAt(0).toUpperCase()}${provider.slice(1)}` : provider;
